import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { GoArrowSwitch } from "react-icons/go";
import * as s from "./SubStyled";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useMenu } from "hook/useMenu";
import { Account } from "api/mappers/accountMapper";
import { BasePagination } from "api/interfaces/commonInterface.interface";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { getSwitchAccountList, postAccountFavorite } from "api/accountAPI";
import Spinner from "components/atoms/loader/Spinner";
import SearchInput from "components/atoms/input/search/SearchInput";
import NoData from "components/templates/nopage/NoData";
import { COLORS } from "styles/colors";
import { useQuery } from "react-query";
import { TiStarFullOutline } from "react-icons/ti";
import AccountId from "components/atoms/text/labels/AccountId";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  checkIsSuspended,
  filterListGetPriority,
  getPriority,
  levelToString,
  selectAccountsForCollapsedDropdown,
} from "utils/AccountUtil";
import * as mediaQuery from "components/MediaQuery";
import { useProSidebar } from "react-pro-sidebar";
import { setIsPopup } from "redux/reducers/menu/userPopup";
import useStatusMutation from "hook/useStatusMutation";
import useApiError from "hook/useApiError";
import { iconSize } from "./UserPopup";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { FaRegCopy } from "react-icons/fa";
import { WithTooltip } from "components/blocks/WithTooltip";
import { handleCopyClipBoard } from "utils/functions";

const maxCount = 8;

export default function SwitchOrgDropdown({
  setIsSwitchAccount,
  isSwitchAccount,
}: {
  setIsSwitchAccount: (isSwitchAccount: boolean) => void;
  isSwitchAccount: boolean;
}) {
  const { navigateSwitchAccount } = useMenu({});
  const { collapsed, collapseSidebar } = useProSidebar();
  const dispatch = useAppDispatch();
  const { lang, notifyError } = useApiError();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const closeTimer = useRef<NodeJS.Timeout | null>(null);

  const onClickAccount = async (account: Account) => {
    if (checkIsSuspended(account) === SUSPENSION_STATUS.DISABLED) {
      return;
    }
    if (
      selectedAccount.accountId === account.accountId &&
      !selectedAccount.isHidden
    ) {
      dispatch(setIsPopup());
      if (!collapsed) collapseSidebar();
      return;
    }
    navigateSwitchAccount(
      {
        accountId: account.accountId,
        onSuccess: () => {
          dispatch(setIsPopup());
          if (!collapsed) collapseSidebar();
        },
      },
      false
    );
  };
  const [accountQueryInfo, setAccountQueryInfo] = useState<BasePagination>({
    ...baseQueryInfo,
    pageLimit: 10000,
  });
  const queryKey = useQueryKey(accountQueryInfo);

  const getAccountSwitch = useQuery(
    ["accountSwitch", queryKey, selectedAccount.accountId],
    () =>
      getSwitchAccountList({
        payload: accountQueryInfo,
      }),

    {
      retry: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      onError: (e: any) => {
        console.log(e, "e");
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["organizationListUpdated"] as StompDateSignalKey[],
    refetch: getAccountSwitch.refetch,
  });
  const [isViewAll, setIsViewAll] = useState(false);
  const isMobile = mediaQuery.useIsMobile();
  const popupMenuRef = useRef<HTMLLIElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const onMouseEnter = (isLoginTo: boolean) => {
    if (isLoginTo) {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
      setIsSwitchAccount(true);
      return;
    }
    closeTimer.current = setTimeout(() => {
      setIsSwitchAccount(false);
    }, 500);
  };

  const handleMouseEnter = (e?: React.MouseEvent<HTMLElement>) => {
    if (isMobile) return;
    e?.stopPropagation();
    e?.preventDefault();
    onMouseEnter(true);
  };

  const handleMouseLeave = (
    e: React.MouseEvent<HTMLElement>,
    target: HTMLElement | null
  ) => {
    if (queryKey.keyword !== "") return;
    if (isMobile) return;
    e.stopPropagation();
    e.preventDefault();
    const relatedTarget = e.relatedTarget;
    if (
      relatedTarget instanceof Node &&
      target &&
      target.contains(relatedTarget)
    )
      return;
    onMouseEnter(false);
  };

  const onClickSearch = (value: string) => {
    setAccountQueryInfo({ ...accountQueryInfo, keyword: value });
  };

  const accounts: Account[] = useMemo(() => {
    const accounts = getAccountSwitch?.data?.result ?? [];
    if (accounts.length === 0) return [];

    if (isViewAll || isMobile || accountQueryInfo.keyword !== "") {
      return [...accounts];
    }
    return selectAccountsForCollapsedDropdown(accounts, maxCount);
  }, [
    accountQueryInfo.keyword,
    getAccountSwitch.data?.result,
    isMobile,
    isViewAll,
  ]);

  useEffect(() => {
    if (!isSwitchAccount) {
      setAccountQueryInfo({ ...baseQueryInfo });
    }
  }, [isSwitchAccount]);
  const mutationUpdateFavorite = useStatusMutation(postAccountFavorite, {
    onSuccess: (res: any) => {
      getAccountSwitch.refetch();
    },
    onError: (err: any) => {},
  });

  const onChangeFavorite = (account: Account) => {
    if (!mutationUpdateFavorite.idle) return;
    if (
      getAccountSwitch.data?.result?.filter((acc: Account) => acc.isFavorite)
        .length === maxCount &&
      !account.isFavorite
    ) {
      notifyError(lang.validateMsg.account.switch.max);
      return;
    }
    mutationUpdateFavorite.mutate({
      accountId: account.accountId,
      isFavorite: !account.isFavorite,
    });
  };

  const switchList = useMemo(() => {
    return getAccountSwitch.data?.result ?? [];
  }, [getAccountSwitch.data?.result]);
  const onClickCopy = (
    e: React.MouseEvent<HTMLButtonElement>,
    accountNumber: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    handleCopyClipBoard(accountNumber);
  };

  return (
    <s.PopupMenu
      ref={popupMenuRef}
      onClick={(e) => {
        e.stopPropagation();
        if (isMobile) {
          setIsSwitchAccount(true);
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={(e) => handleMouseLeave(e, popupMenuRef.current)}
      disabled={false}
      isFocus={isSwitchAccount}
      data-testid="blocks-headernav-sub-switch-org-open"
    >
      <GoArrowSwitch size={iconSize} />
      Login To
      {isSwitchAccount && (
        <s.SwitchAccount.Popup
          ref={popupRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={(e) => handleMouseLeave(e, popupRef.current)}
        >
          <div className="inner">
            {(switchList.length > maxCount ||
              accountQueryInfo.keyword !== "") && (
              <SearchInput
                keyword={accountQueryInfo.keyword}
                placeholder="Search Organization"
                onClickSearch={onClickSearch}
                onFocus={() => onMouseEnter(true)}
                data-testid="blocks-headernav-sub-switch-org-search"
              />
            )}
            {getAccountSwitch.isLoading && (
              <div>
                <Spinner />
              </div>
            )}

            <s.SwitchAccount.List>
              {!getAccountSwitch.isFetching && accounts.length === 0 ? (
                <NoData />
              ) : (
                accounts.map((account: Account, idx: number) => {
                  return (
                    <Fragment key={idx}>
                      {(idx === 0 ||
                        account.level !== accounts[idx - 1].level ||
                        account.parentLevel !==
                          accounts[idx - 1].parentLevel) && (
                        <s.SwitchAccount.Level>
                          {levelToString(
                            account.level === "CP" &&
                              account.parentLevel === "CP"
                              ? "SUB_CP"
                              : account.level
                          )}{" "}
                          (
                          {
                            filterListGetPriority(
                              switchList,
                              getPriority(account)
                            ).length
                          }
                          )
                        </s.SwitchAccount.Level>
                      )}
                      <s.SwitchAccount.Item
                        isCurrent={
                          selectedAccount.accountId === account.accountId
                        }
                        disabled={
                          checkIsSuspended(account) ===
                          SUSPENSION_STATUS.DISABLED
                        }
                        onClick={() => onClickAccount(account)}
                        data-testid={`blocks-headernav-sub-switch-org-item-${idx}`}
                      >
                        <s.SwitchAccount.Label>
                          {switchList.length > maxCount && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onChangeFavorite(account);
                              }}
                              data-testid={`blocks-headernav-sub-switch-org-favorite-${idx}`}
                            >
                              <TiStarFullOutline
                                color={
                                  account.isFavorite
                                    ? "#FFBF00"
                                    : `${COLORS.BLACK}40`
                                }
                                size={18}
                              />
                            </button>
                          )}
                          <span>{account.name}</span>
                        </s.SwitchAccount.Label>
                        <button
                          className="copy-button"
                          onClick={(e) => onClickCopy(e, account.accountNumber)}
                        >
                          <WithTooltip tooltip="Copy ID" showAtTop>
                            <FaRegCopy />
                          </WithTooltip>
                        </button>
                      </s.SwitchAccount.Item>
                    </Fragment>
                  );
                })
              )}
            </s.SwitchAccount.List>
            {switchList.length > maxCount &&
              !isMobile &&
              accountQueryInfo.keyword === "" && (
                <s.SwitchAccount.Toggle
                  onClick={() => setIsViewAll(!isViewAll)}
                  data-testid="blocks-headernav-sub-toggle-view-all"
                >
                  {isViewAll ? "SHOW LESS" : "VIEW ALL"}
                </s.SwitchAccount.Toggle>
              )}

            {accountQueryInfo.keyword !== "" && (
              <s.SwitchAccount.Toggle
                onClick={() =>
                  setAccountQueryInfo({ ...accountQueryInfo, keyword: "" })
                }
                data-testid="blocks-headernav-sub-toggle-clear-all"
              >
                CLEAR ALL
              </s.SwitchAccount.Toggle>
            )}

            {isMobile && accountQueryInfo.keyword === "" && (
              <s.SwitchAccount.Toggle
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setIsSwitchAccount(false);
                }}
                data-testid="blocks-headernav-sub-toggle-close"
              >
                Close
              </s.SwitchAccount.Toggle>
            )}
          </div>
        </s.SwitchAccount.Popup>
      )}
    </s.PopupMenu>
  );
}
